import { useCallback, useState } from 'react';
import { Linkedin, Facebook, Link, Check, TrendingUp } from 'lucide-react';
import './share-score.css';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.atscvchecker.pro';

interface ShareScoreProps {
  score: number;
}

type Tier = 'passing' | 'good' | 'strong' | 'elite';

interface TierData {
  tier: Tier;
  topPercent: number;
  label: string;
  caption: string;
  sharePrompt: string;
}

function getTierData(score: number): TierData {
  if (score >= 90) return {
    tier: 'elite',
    topPercent: score >= 95 ? 5 : 10,
    label: 'ELITE',
    caption: 'Your resume is ready for any major ATS system.',
    sharePrompt: 'Results like this are worth sharing',
  };
  if (score >= 80) return {
    tier: 'strong',
    topPercent: score >= 85 ? 15 : 20,
    label: 'ATS Strong',
    caption: 'Most recruiters\u2019 systems will pass this resume to human review.',
    sharePrompt: 'Let your network know you\u2019re job-ready',
  };
  if (score >= 70) return {
    tier: 'good',
    topPercent: 30,
    label: 'ATS Ready',
    caption: 'Your resume passes ATS filters better than most candidates.',
    sharePrompt: 'Share your result',
  };
  return {
    tier: 'passing',
    topPercent: 40,
    label: 'Improving',
    caption: 'You\u2019re on the right track. Keep optimizing.',
    sharePrompt: 'Share your progress',
  };
}

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const CONFETTI_COUNT = 8;

/**
 * ShareScore — achievement card with tier-based intensity.
 * Shown when score >= 60. Elite tier (90+) gets confetti animation.
 */
export function ShareScore({ score }: ShareScoreProps) {
  const [copied, setCopied] = useState(false);

  if (score < 60) return null;

  const { tier, topPercent, label, caption, sharePrompt } = getTierData(score);

  const shareUrl = `${SITE_URL}?utm_source=share&utm_medium=social&utm_content=score`;
  const shareText = `My resume scored ${score}% on ATS compatibility! Check yours for free:`;

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  }, [shareText, shareUrl]);

  const circumference = 2 * Math.PI * 42;
  const dashOffset = circumference * (1 - score / 100);

  return (
    <div className={`share-card share-card--${tier}`} role="region" aria-label="Share your score">
      {/* Confetti — elite only */}
      {tier === 'elite' && (
        <div className="share-card__confetti" aria-hidden="true">
          {Array.from({ length: CONFETTI_COUNT }, (_, i) => (
            <span key={i} className="share-card__particle" />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="share-card__header">
        <div className="share-card__brand">
          <TrendingUp size={14} />
          <span>ATS Compatibility Score</span>
        </div>
        {(tier === 'elite' || tier === 'strong') && (
          <span className="share-card__tier-badge">{label}</span>
        )}
      </div>

      {/* Score row */}
      <div className="share-card__score-row">
        <div className="share-card__ring" aria-label={`ATS score: ${score} out of 100`}>
          <svg viewBox="0 0 96 96" className="share-card__ring-svg">
            <circle cx="48" cy="48" r="42" className="share-card__ring-track" />
            <circle
              cx="48" cy="48" r="42"
              className="share-card__ring-fill"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          </svg>
          <span className="share-card__ring-value">{score}<span>%</span></span>
        </div>
        <div className="share-card__score-info">
          <span className="share-card__rank">Top {topPercent}% of resumes</span>
          <p className="share-card__caption">{caption}</p>
        </div>
      </div>

      {/* Share prompt + actions */}
      <div className="share-card__footer">
        <span className="share-card__prompt">{sharePrompt}</span>
        <div className="share-card__actions">
          <a href={linkedInUrl} target="_blank" rel="noopener noreferrer"
            className="share-card__btn share-card__btn--linkedin"
            aria-label="Share on LinkedIn">
            <Linkedin size={16} /> LinkedIn
          </a>
          <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
            className="share-card__btn share-card__btn--icon"
            aria-label="Share on Facebook" title="Facebook">
            <Facebook size={16} />
          </a>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer"
            className="share-card__btn share-card__btn--icon"
            aria-label="Share on X" title="X">
            <XIcon />
          </a>
          <button onClick={handleCopy}
            className={`share-card__btn share-card__btn--icon ${copied ? 'share-card__btn--copied' : ''}`}
            aria-label={copied ? 'Copied!' : 'Copy link'}
            title={copied ? 'Copied!' : 'Copy link'}>
            {copied ? <Check size={16} /> : <Link size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
